import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BrandLogo } from '../../shared/components/BrandLogo';
import { Icon } from '../../shared/components/Icon';
import { LanguageSelect } from '../../shared/components/LanguageSelect';
import { PoweredBy } from '../../shared/components/ui';
import { useTheme } from '../../shared/theme/theme';
import { useAuth } from './AuthContext';

export function TenantSelectionPage({
  onSelected,
}: {
  readonly onSelected?: () => void;
} = {}) {
  const { t } = useTranslation();
  const { tenants, selectTenant, logout, userLabel, session } = useAuth();
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const { theme, toggle: toggleTheme } = useTheme();

  const select = async (tenantId: string) => {
    setSelectingId(tenantId);
    setFailed(false);
    try {
      await selectTenant(tenantId);
      onSelected?.();
    } catch {
      setFailed(true);
    } finally {
      setSelectingId(null);
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-card tenant-card" aria-labelledby="tenant-title">
        <header className="auth-card__header">
          <span className="brand__mark" aria-hidden="true"><BrandLogo /></span>
          <div>
            <p className="brand-tagline">{t('app.tagline')}</p>
            <p className="eyebrow">{userLabel}</p>
            <h1 id="tenant-title">{t('auth.selectTenantTitle')}</h1>
            <p>{t('auth.selectTenantSubtitle')}</p>
          </div>
          <div className="auth-preferences">
            <LanguageSelect />
            <button
              className="theme-switch"
              type="button"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')}
            >
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
            </button>
          </div>
        </header>

        {tenants.length > 0 ? (
          <ul className="tenant-list">
            {tenants.map((tenant) => (
              <li key={tenant.tenantId}>
                <button type="button" onClick={() => void select(tenant.tenantId)} disabled={selectingId !== null}>
                  <span><strong>{tenant.displayName}</strong><small><bdi dir="ltr">{tenant.slug}</bdi></small></span>
                  <span>{selectingId === tenant.tenantId ? t('auth.selecting') : t('auth.openTenant')}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : <p className="auth-empty" role="status">{t('auth.noTenants')}</p>}

        {failed && <p className="auth-error" role="alert">{t('auth.tenantError')}</p>}
        <div className="tenant-card__actions">
          {session?.isSuperAdmin && <Link className="button button--primary" to="/platform"><Icon name="settings" />{t('auth.adminConsole')}</Link>}
          {session?.canCreateInvitations && <Link className="button button--secondary" to="/invitations"><Icon name="send" />{t('invitations.title')}</Link>}
          <button className="button button--secondary auth-logout" type="button" onClick={() => void logout()}>{t('auth.logout')}</button>
        </div>
      </section>
      <PoweredBy />
    </main>
  );
}
